import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { enabledChains, byId } from "@/lib/chains";
import type { ChainConfig } from "@atelier/config";

type ChainState = {
  activeChainId: number;
  setActiveChainId: (id: number) => void;
  activeChain: () => ChainConfig | undefined;
};

const defaultId = Number(import.meta.env.VITE_DEFAULT_CHAIN_ID ?? enabledChains()[0]?.chainId ?? 0);

// Persist the chain selection across reloads — without it, every refresh
// snaps back to VITE_DEFAULT_CHAIN_ID and a creator working on a non-default
// chain has to re-pick it every time.
export const useChainStore = create<ChainState>()(
  persist(
    (set, get) => ({
      activeChainId: defaultId,
      setActiveChainId: (id) => set({ activeChainId: id }),
      activeChain: () => byId(get().activeChainId),
    }),
    {
      name: "atelier.activeChain",
      storage: createJSONStorage(() => localStorage),
      // Only persist the id; the resolver function is stateless and re-binds
      // to the current `byId` import on every load.
      partialize: (s) => ({ activeChainId: s.activeChainId }),
      // Drop persisted ids that don't correspond to any currently-enabled
      // chain (e.g. user switches CHAINS env), falling back to default.
      merge: (persisted, current) => {
        const p = persisted as Partial<ChainState> | undefined;
        const enabled = enabledChains().some((c) => c.chainId === p?.activeChainId);
        return {
          ...current,
          activeChainId: enabled && p?.activeChainId ? p.activeChainId : current.activeChainId,
        };
      },
    },
  ),
);
